<template>
  <Transition name="fade">
    <button
      v-if="visible"
      class="back-top"
      @click="scrollToTop"
      :aria-label="
        $t('hui-dao-ding-bu-dang-qian-xian-shi-viewcount-xiang-gong-total-domtotalcount-xiang', [
          viewCount,
          total || domTotalCount,
        ])
      "
      :disabled="scrolling"
      type="button"
    >
      <Transition name="to-up">
        <IconBackTop v-if="!scrolling" size="22" class="back-top__icon" />
        <div v-else class="back-top__counter">
          <Text variant="12" color="t1" weight="medium" as="div" class="back-top__counter-value">{{
            viewCount
          }}</Text>
          <div class="back-top__counter-divider"></div>
          <Text variant="12" color="t1" as="div" class="back-top__counter-total">{{
            total || domTotalCount
          }}</Text>
        </div>
      </Transition>
    </button>
  </Transition>
</template>
<script lang="ts" setup>
import IconBackTop from '@rendora/design/assets/back-top.svg?component';
import { Text } from '@rendora/design/components/text/index';
import { debounce, throttle } from 'lodash-es';
import { nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue';

interface BackTopProps {
  scrollContainer: string | (() => HTMLElement | null); // 滚动容器
  item: string; // 子元素选择器
  top?: number; // 滚动到顶部位置
  visibilityHeight?: number; // 显示阈值
  total?: number; // 总项目数
  smooth?: boolean; // 是否平滑滚动
  duration?: number; // 滚动动画时长(ms)
  throttleDelay?: number; // 节流延迟(ms)
  debounceDelay?: number; // 防抖延迟(ms)
}

const props = withDefaults(defineProps<BackTopProps>(), {
  top: 0,
  visibilityHeight: 400,
  smooth: true,
  duration: 500,
  throttleDelay: 200,
  debounceDelay: 800,
});

const visible = ref(false);
const scrolling = ref(false);
const viewCount = ref(0);
const domTotalCount = ref(0);

const getContainer = (): HTMLElement | null => {
  try {
    if (typeof props.scrollContainer === 'string') {
      const element = document.querySelector(props.scrollContainer);
      return element as HTMLElement | null;
    }
    return props.scrollContainer();
  } catch (error) {
    console.warn('BackTop: 获取滚动容器失败', error);
    return null;
  }
};

const calVisible = throttle(() => {
  const { visibilityHeight } = props;
  const container = getContainer();
  if (container) {
    const scrollTop = container.scrollTop;
    visible.value = scrollTop >= visibilityHeight;
  }
}, props.throttleDelay);

const calStopScroll = debounce(() => {
  scrolling.value = false;
}, props.debounceDelay);

const calCount = throttle(() => {
  const container = getContainer();
  const allItems = container?.querySelectorAll(props.item);
  const containerBottom = container?.getBoundingClientRect().bottom || 0;
  domTotalCount.value = allItems?.length || 0;
  let visibleCount = 0;
  allItems?.forEach(item => {
    if (item.getBoundingClientRect().bottom < containerBottom || 0) {
      visibleCount++;
    }
  });
  viewCount.value = visibleCount;
}, 100);

const handleScroll = () => {
  calVisible();
  if (!scrolling.value) {
    scrolling.value = true;
  }
  calStopScroll();
  calCount();
};

const scrollRemove = () => {
  const container = getContainer();
  calVisible.cancel();
  calStopScroll.cancel();
  if (container) {
    container.removeEventListener('scroll', handleScroll);
  }
};

const bindScrollEvent = () => {
  const container = getContainer();
  if (!container) {
    console.warn('BackTop: 无法找到滚动容器，请检查 scrollContainer 配置');
    return;
  }

  container.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
};

const scrollToTop = () => {
  const container = getContainer();
  if (!container) {
    console.warn('BackTop: 无法找到滚动容器');
    return;
  }

  try {
    if (props.smooth) {
      container.scrollTo({
        top: props.top,
        behavior: 'smooth',
      });
    } else {
      container.scrollTo({
        top: props.top,
      });
    }
  } catch (error) {
    console.warn('BackTop: 滚动到顶部失败', error);
  }
};

watch(
  () => getContainer(),
  () => {
    scrollRemove();
    nextTick(() => {
      bindScrollEvent();
    });
  },
);

onMounted(() => {
  nextTick(() => {
    bindScrollEvent();
  });
});

onActivated(() => {
  nextTick(() => {
    bindScrollEvent();
  });
});

onDeactivated(() => {
  scrollRemove();
});

onBeforeUnmount(() => {
  scrollRemove();
});
</script>
<style lang="scss" scoped>
.back-top {
  position: fixed;
  bottom: 24px;
  right: 10px;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: var(--x-back-1);
  color: var(--x-text-1);
  outline: 1px solid var(--x-back-5);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--x-shadow);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--x-shadow);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color, var(--x-purple-6));
    outline-offset: 2px;
  }

  &__icon {
    position: absolute;
  }

  &__counter {
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 12px;
  }

  &__counter-value {
    // Text 组件已处理颜色
  }

  &__counter-divider {
    background: var(--x-purple-6);
    height: 1px;
    width: 22px;
  }

  &__counter-total {
    // Text 组件已处理颜色
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.to-up-enter-active,
.to-up-leave-active {
  transition: transform 0.25s ease-out;
}

.to-up-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.to-up-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
</style>
